import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Input, Select, Tooltip, Button, Checkbox } from "antd";
import {
  SearchOutlined, ReloadOutlined, TeamOutlined, RightOutlined,
  LoadingOutlined, CheckCircleOutlined, DownloadOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import {
  fetchSmartSearchCustomers, fetchSmartSearchMeta, exportSmartSearchCustomers,
} from "../services/smartSearchService";
import { tierTone, toOptions } from "./planShared";
import {
  BRAND, INK, TONE, chip, statusChip, titleCase, num, inr, dateOnly, isUnlimited,
  QuotaBar, cardStyle, Skeleton, PanelEmpty, PanelError,
  useInfiniteList, useEndSentinel, useDebounced, SS_CSS,
  paymentOf, hasTax,
} from "./customerShared";

/**
 * Everyone on a Smart Search plan.
 *
 * The row is built around the one question this screen exists to answer: is
 * this customer getting value, and are they close to their ceiling. Hence the
 * quota bar rather than two more numeric columns — a fraction read against a
 * bar is faster than a fraction read on its own.
 */

const PAGE_SIZE = 15;

const STATUSES = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "canceled", label: "Canceled" },
  { value: "expired", label: "Expired" },
];

const th = {
  textAlign: "left", padding: "12px 16px", fontSize: 10.5, fontWeight: 700,
  color: "#8b95a8", textTransform: "uppercase", letterSpacing: "0.5px",
  background: "#fafbff", borderBottom: "1px solid #eef0f6", whiteSpace: "nowrap",
};
const td = { padding: "13px 16px", borderBottom: "1px solid #f6f8fc", fontSize: 12.5 };

/**
 * The export file.
 *
 * Every field is quoted and inner quotes doubled, because a company name with a
 * comma in it would otherwise shift every column after it by one. The figures
 * are written raw — unformatted numbers, no ₹ — so the file opens as numbers in
 * a spreadsheet rather than as text.
 */
const CSV_COLUMNS = [
  ["Customer name", (r) => r.customer_name ?? r.name],
  ["Email", (r) => r.customer_email ?? r.email],
  ["Mobile", (r) => r.mobile ?? r.customer_mobile],
  ["Plan", (r) => r.plan_name],
  ["Tier", (r) => r.tier ?? r.plan_tier],
  ["Status", (r) => r.status],
  ["Billing", (r) => r.billing_period],
  ["Purchase type", (r) => r.purchase_type],
  ["Cycle start", (r) => r.cycle_start],
  ["Subscription end", (r) => r.subscription_end_date],
  ["Plan price", (r) => paymentOf(r).base],
  ["GST %", (r) => paymentOf(r).percent],
  ["GST amount", (r) => paymentOf(r).tax],
  ["Total", (r) => paymentOf(r).total],
  // One cell, because "used of limit" is one fact and splitting it across two
  // columns makes a reader rebuild it.
  ["Quota used/limit", (r) => (isUnlimited(r.monthly_quota)
    ? `${r.searches_used ?? 0} / Unlimited`
    : `${r.searches_used ?? 0} / ${r.monthly_quota ?? ""}`)],
  ["Watches", (r) => r.watches_count],
  ["Created at", (r) => r.created_at],
];

const downloadCsv = (rows) => {
  const table = [
    CSV_COLUMNS.map(([h]) => h),
    ...rows.map((r) => CSV_COLUMNS.map(([, read]) => {
      const v = read(r);
      return v === null || v === undefined ? "" : v;
    })),
  ];
  const csv = table
    .map((line) => line.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
    .join("\n");

  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `smart_search_customers_${new Date().toISOString().split("T")[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const Avatar = ({ name }) => (
  <span style={{
    width: 32, height: 32, borderRadius: 10, flexShrink: 0,
    background: "linear-gradient(135deg,#ede9fe,#e0e7ff)", color: BRAND,
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    fontSize: 12.5, fontWeight: 700,
  }}>
    {String(name || "?").trim().charAt(0).toUpperCase()}
  </span>
);

export default function ManageSmartSearchCustomers() {
  const navigate = useNavigate();
  const location = useLocation();
  /* Arriving from a customer's Smart Search badge seeds the box, so the list
     opens showing only them — and the term is visible and clearable rather
     than a hidden filter leaving an unexplained one-row list. */
  const [search, setSearch] = useState(location.state?.search || "");
  const [status, setStatus] = useState("all");
  const [tier, setTier] = useState("all");
  const term = useDebounced(search);

  const fetcher = useCallback(
    (page, limit) => fetchSmartSearchCustomers({ page, limit, search: term, status, tier }),
    [term, status, tier],
  );

  const list = useInfiniteList(fetcher, { deps: [term, status, tier], perPage: PAGE_SIZE });

  /* Watched rather than listened for: the sentinel reports when it is actually
     on screen, so there is no scroll event to throttle and nothing recomputed
     between pages. It starts the next page while the end is still a screen
     away, so the list usually grows before the reader reaches the bottom. */
  const endRef = useEndSentinel(list.loadMore, list.hasMore && !list.error);

  /* Every tier that exists, from the plan catalogue — not the tiers on screen.
     Deriving it from the loaded rows only ever offered what was already
     visible, so with one Firm customer the filter's only choice was "Firm",
     which filters nothing. Picking a tier nobody is on is a fair thing to ask
     and a useful answer: it says so.

     Cached in the service, so this costs one call for the life of the page. */
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchSmartSearchMeta().then((m) => { if (alive) setMeta(m || {}); });
    return () => { alive = false; };
  }, []);

  const tierOptions = useMemo(() => {
    const fromMeta = toOptions(meta?.tiers || [], { label: titleCase });
    if (fromMeta.length) return fromMeta;
    /* Meta unreachable — fall back to what the rows show, which is thin but
       better than a filter with nothing in it. */
    const seen = [];
    list.rows.forEach((r) => {
      const t = r.tier || r.plan_tier;
      if (t && !seen.some((o) => o.value === t)) seen.push({ value: t, label: titleCase(t) });
    });
    return seen;
  }, [meta, list.rows]);

  const openRow = (row) => navigate(
    `/cms/smart-search/customers/${row.id ?? row.subscription_id ?? row.user_id}`,
    { state: { customerName: row.customer_name || row.name } },
  );

  // Rows accumulate, so the position in the loaded list is the number.
  const rowNumber = (i) => i + 1;

  // ── Selection ──
  const idOf = (r) => r.id ?? r.subscription_id ?? r.user_id;
  const [selected, setSelected] = useState([]);
  const [exporting, setExporting] = useState(false);
  /* Off until asked for. Checkboxes on every row all the time is a column of
     furniture for a thing most visits never do, and it makes the row's real
     action — open the customer — compete with a control nobody invoked. */
  const [exportMode, setExportMode] = useState(false);

  const leaveExportMode = () => { setExportMode(false); setSelected([]); };

  /* A different filter is a different list, so a selection made against the old
     one is not carried into it — exporting rows the reader can no longer see is
     the kind of surprise an export must not have. */
  useEffect(() => { setSelected([]); }, [term, status, tier]);

  const loadedIds = useMemo(() => list.rows.map(idOf), [list.rows]);
  const allOnPageSelected = loadedIds.length > 0 && loadedIds.every((id) => selected.includes(id));
  const someSelected = selected.length > 0;

  const toggleOne = (id) => setSelected((prev) => (prev.includes(id)
    ? prev.filter((x) => x !== id)
    : [...prev, id]));

  const toggleAll = () => setSelected(allOnPageSelected ? [] : loadedIds);

  const runExport = async () => {
    setExporting(true);
    try {
      /* With nothing ticked the export is everything the current filters match,
         not the rows that happen to be loaded — an infinite list has no natural
         "all on screen". */
      const rows = await exportSmartSearchCustomers(
        someSelected
          ? { ids: selected.join(",") }
          : { search: term, status, tier },
      );
      if (!rows.length) {
        toast.info("Nothing to export for this selection.");
        return;
      }
      downloadCsv(rows);
      toast.success(`Exported ${rows.length} customer${rows.length === 1 ? "" : "s"}.`);
      leaveExportMode();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          <style>{SS_CSS}</style>
          <div style={{
            background: "#f6f7fc", margin: "-24px -24px 0", padding: "26px 28px",
            minHeight: "100vh", fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
          }}>

            {/* ── Header ── */}
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18, flexWrap: "wrap" }}>
              <div style={{
                width: 42, height: 42, borderRadius: 13,
                background: `linear-gradient(135deg, ${BRAND}, #7c3aed)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#fff", fontSize: 17, boxShadow: "0 6px 18px rgba(91,72,232,0.3)",
              }}>
                <TeamOutlined />
              </div>
              <div>
                <div style={{ fontSize: 19, fontWeight: 700, color: INK.strong }}>Smart Search Customers</div>
                <div style={{ fontSize: 12.5, color: INK.muted, marginTop: 2 }}>
                  Who is on which plan, and what they have used
                </div>
              </div>
              {list.loaded && !list.error && (
                <span style={{ marginLeft: 6 }}>
                  {chip(`${num(list.total)} total`, TONE.indigo)}
                </span>
              )}

              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
                {exportMode ? (
                  <>
                    <Button
                      onClick={toggleAll}
                      style={{
                        height: 38, borderRadius: 10, border: "1px solid #c7d2fe",
                        background: "#f0f4ff", color: BRAND, fontWeight: 600,
                      }}
                    >
                      {allOnPageSelected ? "Clear selection" : `Select all (${loadedIds.length})`}
                    </Button>
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={runExport}
                      loading={exporting}
                      disabled={list.loaded && list.total === 0}
                      style={{
                        height: 38, borderRadius: 10, border: "none",
                        background: `linear-gradient(135deg, ${BRAND}, #7c3aed)`,
                        color: "#fff", fontWeight: 600,
                        boxShadow: "0 4px 14px rgba(91,72,232,0.28)",
                      }}
                    >
                      {/* The label says exactly what the file will contain, so a
                          tick never changes the scope behind the reader's back. */}
                      {someSelected ? `Export (${selected.length})` : `Export all (${num(list.total)})`}
                    </Button>
                    <Button
                      onClick={leaveExportMode}
                      disabled={exporting}
                      style={{ height: 38, borderRadius: 10, fontWeight: 600, color: INK.body }}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={() => setExportMode(true)}
                    disabled={list.loaded && list.total === 0}
                    style={{
                      height: 38, borderRadius: 10, border: "none",
                      background: `linear-gradient(135deg, ${BRAND}, #7c3aed)`,
                      color: "#fff", fontWeight: 600,
                      boxShadow: "0 4px 14px rgba(91,72,232,0.28)",
                    }}
                  >
                    Export
                  </Button>
                )}
              </div>
            </div>

            {/* ── Controls ── */}
            <div style={{
              display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap",
              background: "#fff", border: "1px solid #eceef5", borderRadius: 14,
              padding: "12px 14px", boxShadow: "0 1px 3px rgba(15,23,42,0.03)",
            }}>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
                prefix={<SearchOutlined style={{ color: "#c0c6d4" }} />}
                placeholder="Search by name or email"
                style={{ width: 290, borderRadius: 9 }}
              />
              <Select
                value={status}
                onChange={setStatus}
                options={STATUSES}
                style={{ width: 160 }}
              />
              <Select
                value={tier}
                onChange={setTier}
                style={{ width: 160 }}
                loading={!meta}
                options={[{ value: "all", label: "All tiers" }, ...tierOptions]}
              />
              <Tooltip title="Reload">
                <Button
                  icon={<ReloadOutlined />} onClick={list.reload} loading={list.loading}
                  style={{ marginLeft: "auto", borderRadius: 9 }}
                />
              </Tooltip>
            </div>

            {/* ── Table ── */}
            <div style={{ ...cardStyle, overflow: "hidden" }}>
              {list.loading && !list.loaded ? (
                <div style={{ padding: 16 }}><Skeleton rows={8} /></div>
              ) : list.error && list.rows.length === 0 ? (
                <div style={{ padding: 18 }}>
                  <PanelError message={list.error} onRetry={list.reload} />
                </div>
              ) : list.rows.length === 0 ? (
                <div style={{ padding: 18 }}>
                  <PanelEmpty message={
                    term || status !== "all" || tier !== "all"
                      ? "No customer matches these filters."
                      : "Nobody is on a Smart Search plan yet."
                  } />
                </div>
              ) : (
                <>
                  <div className="ss-sc" style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1420 }}>
                      <thead>
                        <tr>
                          {exportMode && (
                            <th style={{ ...th, width: 44 }}>
                              <Checkbox
                                checked={allOnPageSelected}
                                indeterminate={someSelected && !allOnPageSelected}
                                onChange={toggleAll}
                              />
                            </th>
                          )}
                          {["#", "Customer", "Plan", "Status", "Quota", "Watches",
                            "Billing", "Plan price", "GST %", "GST amount", "Total",
                            "Subscription end", ""]
                            .map((h, i) => (
                              <th key={i} style={{
                                ...th,
                                textAlign: ["Watches", "Plan price", "GST %", "GST amount", "Total"]
                                  .includes(h) ? "right" : "left",
                              }}>
                                {h}
                              </th>
                            ))}
                        </tr>
                      </thead>
                      <tbody>
                        {list.rows.map((r, i) => (
                          <tr
                            key={r.id ?? r.subscription_id ?? i}
                            className="ss-row"
                            onClick={() => (exportMode ? toggleOne(idOf(r)) : openRow(r))}
                            style={{ cursor: "pointer", opacity: list.loading ? 0.55 : 1 }}
                          >
                            {exportMode && (
                              /* Stops the click from opening the row: ticking a
                                 box and navigating away are different intents. */
                              <td style={td} onClick={(e) => e.stopPropagation()}>
                                <Checkbox
                                  checked={selected.includes(idOf(r))}
                                  onChange={() => toggleOne(idOf(r))}
                                />
                              </td>
                            )}
                            <td style={{ ...td, color: INK.faint, fontSize: 11.5 }}>{rowNumber(i)}</td>

                            <td style={td}>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <Avatar name={r.customer_name || r.name} />
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontWeight: 700, color: INK.strong, whiteSpace: "nowrap" }}>
                                    {r.customer_name || r.name || "—"}
                                  </div>
                                  <div style={{
                                    fontSize: 11.5, color: INK.muted, maxWidth: 210,
                                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                  }}>
                                    {r.customer_email || r.email || "—"}
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td style={td}>
                              <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                                <span style={{ color: INK.strong, fontWeight: 600 }}>
                                  {r.plan_name || "—"}
                                </span>
                                {(r.tier || r.plan_tier) && chip(r.tier || r.plan_tier, tierTone(r.tier || r.plan_tier))}
                              </div>
                            </td>

                            <td style={td}>{statusChip(r.status)}</td>

                            <td style={td}>
                              <QuotaBar used={r.searches_used} quota={r.monthly_quota} />
                            </td>

                            <td style={{ ...td, textAlign: "right", color: INK.body, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                              {num(r.watches_count)}
                            </td>

                            <td style={td}>
                              <div style={{ color: INK.body }}>{titleCase(r.billing_period) || "—"}</div>
                              {r.purchase_type && (
                                <div style={{ fontSize: 11, color: INK.muted, marginTop: 2 }}>
                                  {titleCase(r.purchase_type)}
                                </div>
                              )}
                            </td>

                            {/* Four columns, because they are four different
                                figures and a reader compares each one down the
                                page. Read off `payment` — the real charge, which
                                an upgrade rewrites — with the subscription's own
                                snapshot only as a fallback. Nothing is computed
                                here; every value is the API's own. */}
                            {(() => {
                              const pay = paymentOf(r);
                              const money = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" };
                              return (
                                <>
                                  <td style={{ ...money, color: INK.body }}>{inr(pay.base)}</td>
                                  <td style={{ ...money, color: INK.body }}>
                                    {pay.percent ? `${pay.percent}%` : "—"}
                                  </td>
                                  <td style={{ ...money, color: INK.body }}>
                                    {hasTax(pay.tax) ? inr(pay.tax) : "—"}
                                  </td>
                                  <td style={{ ...money, fontWeight: 700, color: INK.strong }}>
                                    {inr(pay.total)}
                                    {!pay.charged && pay.total !== null && pay.total !== undefined && (
                                      // The plan's price, not a charge. Said rather
                                      // than left to look like money received.
                                      <div style={{ fontSize: 10, color: INK.faint, fontWeight: 400 }}>
                                        not charged yet
                                      </div>
                                    )}
                                  </td>
                                </>
                              );
                            })()}

                            {/* One date. Access ends when the subscription does,
                                so two columns were saying the same thing. */}
                            <td style={{ ...td, color: INK.body, whiteSpace: "nowrap" }}>
                              {dateOnly(r.subscription_end_date)}
                            </td>



                            <td style={{ ...td, textAlign: "right", color: INK.faint }}>
                              <RightOutlined style={{ fontSize: 11 }} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* The sentinel sits inside the scrolling flow, below the last
                      row, so coming into view is exactly "the reader reached the
                      end". */}
                  <div ref={endRef} />

                  <div style={{
                    padding: "16px 24px", borderTop: "1px solid #f0f0f0",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    gap: 9, background: "#fafbff", fontSize: 12.5, color: INK.muted,
                    minHeight: 52,
                  }}>
                    {list.error ? (
                      <>
                        <span style={{ color: "#9a3412" }}>{list.error}</span>
                        <Button size="small" onClick={list.loadMore} style={{ borderRadius: 8 }}>
                          Retry
                        </Button>
                      </>
                    ) : list.loading ? (
                      <>
                        <LoadingOutlined style={{ color: BRAND }} spin />
                        Loading more…
                      </>
                    ) : list.hasMore ? (
                      // The observer will fire on its own; this is for the case
                      // where it cannot, such as a list shorter than the viewport.
                      <Button size="small" onClick={list.loadMore} style={{ borderRadius: 8 }}>
                        Load more
                      </Button>
                    ) : (
                      <>
                        <CheckCircleOutlined style={{ color: TONE.green.color }} />
                        All {num(list.total)} customers loaded
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
